import { useState, useMemo } from 'react';
import type { ApiEndpoints, ProjectDataLLPEvent } from '@avisoma//shared';
import { FaArrowRight } from 'react-icons/fa';
import clsx from 'clsx';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { Input } from '../../catalyst/input';
import SortableTable from '../../components/SortableTable';
import type { SortableTableColumn } from '../../components/SortableTable';
import { ExportAsCSVButton } from '../../components/ExportAsCSVButton';
import { createStableRowKey } from '../../lib/stableRowKey';
import { SelectedPartEvents } from './SelectedPartEvents';
import { PartNumberDisagreementWarning } from './PartNumberDisagreementWarning';

interface LLPEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

type LLPPart = ProjectDataLLPEvent['parts'][number];

export function LLPEvents({ response }: LLPEventsProps) {
  const data = response.item.data?.llpEvents;
  const parts = data?.parts;

  // 'none' = user explicitly deselected; null = no choice yet → default to first part with events
  const [selection, setSelection] = useState<LLPPart | 'none' | null>(null);
  const defaultPart = parts?.find((part) => part.events.length > 0) ?? null;
  const selectedPart = selection === 'none' ? null : (selection ?? defaultPart);

  const togglePart = (part: LLPPart) => setSelection(selectedPart === part ? 'none' : part);

  const [filter, setFilter] = useState('');

  const filteredParts = useMemo(() => {
    if (!parts) return [];
    const query = filter.trim().toLowerCase();
    if (!query) return parts;
    return parts.filter(
      (part) => part.partNumber.toLowerCase().includes(query) || part.serialNumber.toLowerCase().includes(query),
    );
  }, [parts, filter]);

  if (!parts) {
    return null;
  }

  const isMuted = (part: LLPPart) => (part.partMovementPageIds?.length ?? 0) === 0 && selectedPart !== part;

  const columns: SortableTableColumn<LLPPart>[] = [
    {
      accessor: 'partNumber',
      label: 'Part Number',
      sortable: true,
      render: (part) => (
        <div
          className={clsx(
            'inline-flex items-center gap-1 font-medium whitespace-nowrap',
            isMuted(part) && 'text-gray-400 dark:text-zinc-500',
          )}
        >
          {part.partNumber}
          <PartNumberDisagreementWarning part={part} />
        </div>
      ),
    },
    {
      accessor: 'serialNumber',
      label: 'Serial Number',
      sortable: true,
      render: (part) => (
        <div className={clsx(isMuted(part) && 'text-gray-400 dark:text-zinc-500')}>{part.serialNumber}</div>
      ),
    },
    {
      accessor: 'partMovementPageIds',
      label: 'Pages',
      sortable: true,
      sortValue: (part) => part.partMovementPageIds?.length ?? 0,
      render: (part) => (
        <div className={clsx(isMuted(part) && 'text-gray-400 dark:text-zinc-500')}>
          {part.partMovementPageIds?.length ?? 0}
        </div>
      ),
    },
    {
      accessor: 'events',
      label: 'Events',
      sortable: true,
      sortValue: (part) => part.events.length,
      render: (part) => (
        <div
          className={clsx(
            'flex items-center justify-end gap-2',
            isMuted(part) && 'text-gray-400 dark:text-zinc-500',
          )}
        >
          {part.events.length}
          <FaArrowRight className={clsx('size-3', selectedPart === part ? 'visible' : 'invisible')} />
        </div>
      ),
    },
  ];

  return (
    <Card
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">LLP Events</h2>
          <ExportAsCSVButton
            data={parts.flatMap((part, index) => {
              return part.events.map((event) => ({
                index,
                partNumber: part.partNumber,
                serialNumber: part.serialNumber,
                ...event,
              }));
            })}
            columns={[
              { accessor: 'index', label: 'Part Index' },
              { accessor: 'partNumber', label: 'Part Number' },
              { accessor: 'serialNumber', label: 'Serial Number' },
              { accessor: 'date', label: 'Date' },
              { accessor: 'type', label: 'Type' },
              { accessor: 'esn', label: 'ESN' },
              { accessor: 'tsn', label: 'TSN' },
              { accessor: 'csn', label: 'CSN' },
              { accessor: 'msn', label: 'MSN' },
              { accessor: 'name', label: 'Name' },
              { accessor: 'reason', label: 'Reason' },
              { accessor: 'thrust', label: 'Thrust' },
              { accessor: 'operator', label: 'Operator' },
              { accessor: 'aircraftReg', label: 'Aircraft Reg' },
            ]}
            title="LLP Events"
          />
        </div>
      }
    >
      <DescriptionList>
        <DescriptionTerm>Parts</DescriptionTerm>
        <DescriptionDetails>{parts.length}</DescriptionDetails>
      </DescriptionList>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-2">
          <Input
            type="search"
            placeholder="Filter by part or serial number…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter parts"
          />
          <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
            <SortableTable<LLPPart>
              storageId="project-llp-parts"
              columns={columns}
              data={filteredParts}
              rowKey={createStableRowKey(parts)}
              onRowClick={togglePart}
              rowClassName={(part) =>
                selectedPart === part ? 'bg-blue-50 dark:bg-zinc-700/60 text-gray-900 dark:text-zinc-100' : ''
              }
            />
          </div>
        </div>

        <div className="lg:col-span-3">
          <SelectedPartEvents selectedPart={selectedPart} />
        </div>
      </div>
    </Card>
  );
}
